import React, { useContext, useEffect, useState } from "react";
import { Button, Col, Row, Table } from "reactstrap";
import DebounceInput from "react-debounce-input";
import { useParams } from "react-router-dom";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { CartAddModal, CustomerTypeahead, Pager, ServiceThreadSpinner, SortHeader, } from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers, UserContext } from "../utils";

export default function CustomerMachinePartPricing(props) {
   const userCtx = useContext(UserContext);
   const isCustomer = userCtx && userCtx.currentUser && userCtx.currentUser.isAnyCustomerTypeRole;
   const { customerObject } = useParams();
   const [addToCart, setAddToCart] = useState(null);
   const [customer, setCustomer] = useState(null);
   const [data, setData] = useState([]);
   const [downloading, setDownloading] = useState(false);
   const [filter, setFilter] = useState();
   const [initialized, setInitialized] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [page, setPage] = useState(1);
   const [pageSize, setPageSize] = useState(25);
   const [sortDirection, setSortDirection] = useState("asc");
   const [sortField, setSortField] = useState("itemDescription");
   const [totalPages, setTotalPages] = useState();

   useEffect(() => {
      if (!initialized && customerObject) {
         const json = decodeURI(customerObject);
         const opt = JSON.parse(json);
         setCustomer(opt);
         setInitialized(true);
         userCtx.setCustomerFilter(opt); // this is happening on every customer typeahead, doing it here again
         userCtx.loadCart(opt.value);    // this is happening on every customer typeahead, doing it here again
      }
      if (initialized && !loaded) {
         runQuery();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialized, loaded, filter]); // just call loaded from where ever you want

   function downloadPriceListPdf() {
      setDownloading(true);
      let payload = {
         CustomerId: customer.value,
         SortField: "ItemDescription",
         SortDirection: "Asc",
      };
      api
         .postDownloadBinary(
            "Report/CustomerPriceList",
            payload,
            constants.MIME_TYPES.pdf,
            "ServiceThread_Pricing_Data_" +
            dateHelpers.getDateStringForReportName() +
            ".pdf",
            "There was an error downloading the pricing list"
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setDownloading(false));
   }

   function exportXLSX() {
      const payload = {
         CustomerId: _.trim(customer.value),
         SortDirection: sortDirection,
         SortField: sortField,
         ItemNumberSearch: filter,
      };
      setDownloading(true);
      api
         .postGetBlob("Customer/ExportMachinePartXLSX", payload)
         .then((r) => {
            if (r.data) {
               const XLSX_FILENAME = `${payload.CustomerId}_Part_Pricing.xlsx`;
               helpers.downloadExport(
                  r.data,
                  XLSX_FILENAME,
                  constants.MIME_TYPES.xlsx
               );
            } else {
               toast.error(r.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setDownloading(false);
         });
   }

   function handleSearch(val) {
      setFilter(val);
      setLoaded(false);
   }

   function onPageChange(page) {
      setPage(page);
      setLoaded(false);
   }

   function onPageSizeChange(size) {
      setPageSize(size);
      setTotalPages(null);
      onPageChange(1);
   }

   function onSortChange(field, direction) {
      setSortField(field);
      setSortDirection(direction);
      setLoaded(false);
   }

   function runQuery() {
      if (customer === null) {
         setLoaded(true);
         setData([]);
         setTotalPages(null);
         return;
      }

      const payload = {
         CustomerId: customer.value,
         // CustomerId: _.trim(userCtx.customerFilter.value),
         // this code is not yet understood so I commented it out
         PageNumber: page,
         PageSize: pageSize,
         ItemNumberSearch: filter,
      };

      return api
         .post("Customer/GetPaginatedCustomerMachinePartRetailPricing", payload)
         .then((response) => {
            if (response.data.success) {
               const sortedData = _.chain(response.data?.message?.list)
                  .orderBy([sortField], [sortDirection])
                  .drop(pageSize * (page - 1))
                  .take(pageSize)
                  .value();
               setData(sortedData);  // List of CustomerMachinePartPrice.cs
               setTotalPages(response?.data?.message.totalPages);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoaded(true));
   }

   return (
      document.title = `Machine Part Pricing`,
      <DesktopContainer screenName={"Machine Part Pricing"}>
         <div className="filter-header mb-3">
            {!isCustomer ? (
               <Row className="justify-content-center mb-2">
                  <Col xs={1} className="text-end">
                     <h4 className="pt-1 text-white" children="Customer:" />
                  </Col>
                  <Col xs={7}>
                     <CustomerTypeahead
                        value={customer}
                        onChange={(e) => {
                           setCustomer(e);
                           setLoaded(false);
                        }}
                     />
                  </Col>
               </Row>
            ) : null}
            <Row className="justify-content-center mt-2">
               <Col xs={1} className="text-end">
                  <h4 className="pt-1 text-white" children="Search:" />
               </Col>
               <Col xs={7}>
                  <DebounceInput
                     name="searchFilter"
                     className="form-control"
                     onChange={(e) => handleSearch(e.target.value)}
                     value={filter}
                     debounceTimeout={300}
                  />
               </Col>
            </Row>
         </div>
         <ServiceThreadSpinner on={!loaded}>
            <Row className="align-items-center mb-2">
               <Col xxl={9} className="transparent-background">
                  <Pager
                     callBack={onPageChange}
                     sizeCallback={onPageSizeChange}
                     pageNumber={page}
                     pageSize={pageSize}
                     totalPages={totalPages}
                     disabled={props.downloading}
                  />
               </Col>
               <Col className="transparent-background">
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                     <Button
                        title="Download as .xlsx"
                        className="shredButtonExcel float-end ms-2"
                        onClick={exportXLSX}
                        disabled={downloading}
                     >
                        <FontAwesomeIcon icon="file-excel" size="lg" />
                     </Button>
                     {userCtx.currentUser && (userCtx.currentUser.isAPUCS || userCtx.currentUser.isMachinePartsCustomerRole) && (
                        <Button
                           className="shredButtonExcel float-end ms-2"
                           children="Print Customer Price List"
                           onClick={downloadPriceListPdf}
                           disabled={downloading}
                        />
                     )}
                     {data && data.length && props.exportPdfCallback ? (
                        <Button
                           title="Download as .pdf"
                           className="shredButtonExcel float-end"
                           onClick={props.exportPdfCallback}
                           disabled={props.downloading}
                        >
                           <FontAwesomeIcon icon="file-pdf" size="lg" />
                        </Button>
                     ) : null}
                  </div>
               </Col>
            </Row>
            {data.length ? (
               <Table className="data-table">
                  <thead className="table-header">
                     <tr>
                        <th>
                           <SortHeader
                              displayName="Item"
                              field="customerItemNumber"
                              sortDirection={sortDirection}
                              sorted={sortField === "customerItemNumber"}
                              callback={onSortChange}
                           />
                        </th>
                        <th>
                           <SortHeader
                              displayName="Description"
                              field="itemDescription"
                              sortDirection={sortDirection}
                              sorted={sortField === "itemDescription"}
                              callback={onSortChange}
                           />
                        </th>
                        <th>
                           <SortHeader
                              displayName="In Stock"
                              field="stockStatus"
                              sortDirection={sortDirection}
                              sorted={sortField === "stockStatus"}
                              callback={onSortChange}
                           />
                        </th>
                        <th>
                           <SortHeader
                              displayName="List Price"
                              field="listPrice"
                              sortDirection={sortDirection}
                              sorted={sortField === "listPrice"}
                              callback={onSortChange}
                           />
                        </th>
                        <th>
                           <SortHeader
                              displayName="Your Price"
                              field="customerCost"
                              sortDirection={sortDirection}
                              sorted={sortField === "customerCost"}
                              callback={onSortChange}
                           />
                        </th>
                        {userCtx.currentUser &&
                           (userCtx.currentUser.isAPUCS ||
                              userCtx.currentUser.isSewingTech) && <th>Cart</th>}
                     </tr>
                  </thead>
                  <tbody>
                     {_.map(data, (machinePart, index) => (
                        <tr
                           className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                           key={index}
                        >
                           <td className="data-cell">{machinePart.customerItemNumber}</td>
                           <td className="data-cell">{machinePart.itemDescription}</td>
                           <td className="data-cell">{machinePart.stockStatus}</td>
                           <td className="data-cell text-decoration-line-through">
                              {helpers.formatCurrency(machinePart.listPrice)}
                           </td>
                           <td className="data-cell">
                              {helpers.formatCurrency(machinePart.customerCost)}
                           </td>
                           {userCtx.currentUser &&
                              (userCtx.currentUser.isAPUCS ||
                                 userCtx.currentUser.isSewingTech) && (
                                 <td className="data-cell" onClick={() => setAddToCart(machinePart)} >
                                    <span className="shredLink">
                                       <AiOutlineShoppingCart className="cart-icon" />
                                    </span>
                                 </td>
                              )}
                        </tr>
                     ))}
                  </tbody>
                  <tfoot className="table-footer">
                     <tr>
                        <td colSpan="6" />
                     </tr>
                  </tfoot>
               </Table>
            ) : (
               <div className="h5 text-center mt-3">
                  {customer
                     ? "No Machine Parts found for customer"
                     : "Please select a customer"}
               </div>
            )}
            {addToCart ? (
               <CartAddModal
                  item={addToCart}
                  exitCallback={() => setAddToCart(null)}
               />
            ) : null}
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
